import {AlertIcon} from '@primer/octicons-react'
import type {ActiveTurn, MessageAttachment, ResponsePart, ToolCallState, Turn} from '@microsoft/agent-host-protocol'
import {
  describeResolvedInputRequest,
  getTurnError,
  stringOrMarkdownToText,
  type TimelineTurn,
  type SubagentResolver,
} from '@github-ui/ahp-relay/ahp-session-selectors'
import type {RelaySteeringMessage} from '@github-ui/ahp-relay/relay-steering-messages'
import {inlineToolInput} from '@github-ui/ahp-relay/tool-input'
import {
  getCopyableResponseText,
  isTaskCompletePart,
  resolveTaskCompletion,
  type TaskCompletionResolution,
} from '@github-ui/ahp-relay/task-complete'
import {
  Fragment,
  memo,
  useMemo,
  use,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentProps,
} from 'react'
import {announce} from '@github-ui/aria-live'
import {CopyToClipboardIconButton} from '@github-ui/copy-to-clipboard/IconButton'
import {Button, RelativeTime, Tooltip, VisuallyHidden} from '@primer/react'
import {
  GitHubReferenceCards,
  GitHubReferenceClickContext,
} from '@github-ui/copilot-shared-ui/components/GitHubReferenceCards'
import type {ReferenceItem} from '@github-ui/copilot-shared-ui/components/github-references'
import {
  DISPLAY_GITHUB_REFERENCES_TOOL_NAME,
  parseGithubReferencesToolInput,
} from '@github-ui/copilot-shared-ui/components/github-references'
import {ThinkingBlock} from '../ThinkingBlock'
import {
  createToolDisclosureMemoryStore,
  ToolDefaultOpenContext,
  ToolDisclosureMemoryContext,
} from '../tool/ToolPrimitives'
import {useGroupedCodeSearchAnnouncements} from './use-grouped-code-search-announcements'
import {ResolvedAskUser} from '../ResolvedAskUser'
import {UserMessageBubble} from '../UserMessageBubble'
import {AhpAssistantText} from './parts/AhpAssistantText'
import {AhpToolCall, isDeniedCancellation} from './parts/AhpToolCall'
import {TOOLS_HIDDEN_FROM_TIMELINE} from '../../../constants/tool-names'
import {useSubagentPresence} from '../../../ahp/use-ahp-child-session-state'
import {extractUserMessageImages, isPreviewableUserMessageImageAttachment} from './user-message-images'
import {
  extractTurnActor,
  extractTurnContributedInstructions,
  extractTurnMessageType,
  extractTurnSpace,
} from './turn-meta'
import {useActorProfileState, useShouldShowUserMessage, useReadOnlyAhpHistory} from './AhpSessionContext'
import {ResolvedPromptDisplay} from '../ResolvedPromptDisplay'
import turnStyles from '../EventSessionLogs.module.css'
import styles from './AhpTurnView.module.css'
import {Toolbar} from '@github-ui/copilot-chat/components/Toolbar'
import {MessageTokenUsageWithData} from '@github-ui/copilot-chat/components/MessageTokenUsage'
import {AppliedInstructionsMenu} from '@github-ui/copilot-chat/components/AppliedInstructionsDialog'
import {SessionFeedbackButtons} from '../../task-view/feedback/SessionFeedbackButtons'
import {useSessionContext} from '../../../contexts/SessionContext'
import {
  isSystemAuthoredMessage,
  isSystemNotificationMessage,
  parseBackgroundShellNotification,
} from '../../../utils/background-task-notifications'
import type {ModelForTokenUsage} from '@github-ui/copilot-chat/components/MessageTokenUsage'
import {getChannelOrigin} from '../../../types/origin'
import {useBackgroundActivityTaskPresent} from '../../../hooks/use-background-activity-presence'
import {SlackIcon} from '@github-ui/custom-icons/SlackIcon'
import microsoftTeamsIcon from './microsoft-teams.png'
import type {ContributedInstructionsMetadata} from '@github-ui/ahp-session-client/contracts'
import {useSessionLogsContext} from '../Context'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import type {AhpSubagentComponent} from './parts/AhpSubagentReference'
import {LegacyChatInitialMessageContext} from '../../task-view/legacy-chat-initial-message-context'
import type {CopilotResultRenderIdentity} from '@github-ui/copilot-browser-latency/copilot-result-presentation'
import {useCopilotResultElement} from '@github-ui/copilot-browser-latency/use-copilot-result-presentation'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  AhpResponseSection,
  AhpToolCallGroup,
  AhpToolCallSummary,
  useLiveRunCompletionAnnouncements,
  type RegisterGroupTrigger,
} from './parts/AhpToolCallGroup'
import {
  groupResponseParts,
  isFileChangeToolCall,
  segmentResponseParts,
  trailingBlockStart,
  type GroupedResponsePart,
  type ResponsePartEntry,
  type ResponseRun,
  type ResponseSectionItem,
} from './tool-call-groups'
import {TimelineVerbosityContext, type TimelineVerbosity} from './timeline-verbosity'

// AHP enums are const, so compare wire literals (isolatedModules).
const PART_MARKDOWN = 'markdown'
const PART_REASONING = 'reasoning'
const PART_TOOL_CALL = 'toolCall'
const PART_INPUT_REQUEST = 'inputRequest'
const TURN_STATE_ERROR = 'error'
const TURN_STATE_COMPLETE = 'complete'
const TURN_STATE_CANCELLED = 'cancelled'
const NO_PRESENTATION = {turn_id: ''}

const SKILL_CONTEXT_PATTERN = /^\s*<skill-context(?:\s[^>]*)?>[\s\S]*<\/skill-context>\s*$/

/** True when the message text is a runtime-injected skill-context block. */
function isSkillContextMessage(text: string | undefined): boolean {
  return text != null && SKILL_CONTEXT_PATTERN.test(text)
}

/** True for a non-image attachment we can render as a chip. Guards against falsy/malformed entries the same way the image path does, so a bad attachment can't crash the turn render. */
function isChipAttachment(att: MessageAttachment | undefined): att is MessageAttachment {
  return Boolean(att) && !isPreviewableUserMessageImageAttachment(att)
}

/** Non-image attachments, shown as reference chips rather than inline previews. */
export function extractChipAttachments(message: Turn['message'] | undefined): MessageAttachment[] {
  const attachments = message?.attachments
  if (!Array.isArray(attachments)) return []
  return attachments.filter(isChipAttachment)
}

function isErroredTurn(turn: TimelineTurn): turn is Turn {
  return 'state' in turn && turn.state === TURN_STATE_ERROR
}

function isCompletedTurn(turn: TimelineTurn): turn is Turn {
  return 'state' in turn && (turn.state === TURN_STATE_COMPLETE || turn.state === TURN_STATE_CANCELLED)
}

function getTurnCompletedAt(turn: TimelineTurn): string | undefined {
  if (
    !isCompletedTurn(turn) ||
    !turn.startedAt ||
    turn.duration === undefined ||
    !Number.isFinite(turn.duration) ||
    turn.duration < 0
  ) {
    return undefined
  }

  const completedAt = new Date(Date.parse(turn.startedAt) + turn.duration)
  return Number.isFinite(completedAt.getTime()) ? completedAt.toISOString() : undefined
}

function getTurnStatusLabel(turn: TimelineTurn): string {
  if (isErroredTurn(turn)) return 'error'
  if ('state' in turn && turn.state === TURN_STATE_COMPLETE) return 'complete'
  if ('state' in turn && turn.state === TURN_STATE_CANCELLED) return 'cancelled'
  return 'working'
}

function isSuccessfulReferenceCall(toolCall: ToolCallState): boolean {
  return toolCall.status === 'completed' && toolCall.success === true
}

const ResponsePartView = memo(function ResponsePart({
  part,
  presentation,
  chatResource,
  SubagentComponent,
  subagentOffline,
  subagentChatsEnabled,
  resolveSubagent,
  isStreaming,
  readOnlyHistory,
  detailed,
}: {
  part: ResponsePart
  presentation?: CopilotResultRenderIdentity
  chatResource?: string
  SubagentComponent: AhpSubagentComponent
  subagentOffline?: boolean
  subagentChatsEnabled: boolean
  resolveSubagent?: SubagentResolver
  isStreaming?: boolean
  readOnlyHistory: boolean
  detailed?: boolean
}) {
  const terminalRef = useCopilotResultElement(
    presentation ?? NO_PRESENTATION,
    'terminal',
    !readOnlyHistory && Boolean(presentation?.terminal_state && presentation.terminal_state !== 'error'),
  )
  if (part.kind === PART_MARKDOWN) {
    return (
      <AhpAssistantText
        content={part.content}
        presentation={presentation ? {...presentation, part_id: part.id} : undefined}
      />
    )
  }

  if (part.kind === PART_REASONING) {
    return part.content?.trim() ? (
      <ToolDefaultOpenContext value={detailed}>
        <ThinkingBlock reasoningText={part.content} isStreaming={isStreaming} defaultExpanded={detailed} />
      </ToolDefaultOpenContext>
    ) : null
  }

  // `task_complete` never renders as a tool row; TurnResponseContent shows its
  // summary as assistant text when it is the turn's answer.
  if (isTaskCompletePart(part)) return null

  if (part.kind === PART_TOOL_CALL) {
    // The render-only reference tool surfaces as panel-style cards built from
    // its structured input, not as a generic tool-call row.
    if (part.toolCall.toolName === DISPLAY_GITHUB_REFERENCES_TOOL_NAME) {
      if (!isSuccessfulReferenceCall(part.toolCall)) return null
      const toolInput = inlineToolInput('toolInput' in part.toolCall ? part.toolCall.toolInput : undefined)
      const references = parseGithubReferencesToolInput(toolInput)
      return references.length > 0 ? (
        <div className={styles.toolPart} ref={terminalRef}>
          <ClickableGitHubReferenceCards items={references} />
        </div>
      ) : null
    }
    return (
      <div className={styles.toolPart} ref={terminalRef}>
        <AhpToolCall
          toolCall={part.toolCall}
          chatResource={chatResource}
          SubagentComponent={SubagentComponent}
          subagentOffline={subagentOffline}
          subagentChatsEnabled={subagentChatsEnabled}
          defaultExpanded={detailed !== undefined && isFileChangeToolCall(part.toolCall) ? detailed : undefined}
          {...(resolveSubagent ? {resolveSubagent} : {})}
        />
      </div>
    )
  }

  if (part.kind === PART_INPUT_REQUEST) {
    // Pending (unresolved) input requests drive the composer takeover; only the
    // resolved case renders in the turn timeline so the user can see which
    // question was asked and how they answered.
    const resolved = describeResolvedInputRequest(part)
    if (!resolved && readOnlyHistory) {
      return (
        <ResolvedPromptDisplay title={part.request.message ?? 'Input requested'} subtitle="Unanswered in saved history">
          {part.request.questions?.map(question => (
            <p key={question.id}>{question.message}</p>
          ))}
        </ResolvedPromptDisplay>
      )
    }
    if (!resolved) return null
    return (
      <div className={styles.toolPart} ref={terminalRef}>
        <ResolvedAskUser
          question={resolved.question}
          resolvedAnswer={resolved.answer}
          isSkipped={resolved.resolution === 'decline'}
          isAborted={resolved.resolution === 'cancel'}
        />
      </div>
    )
  }

  if (readOnlyHistory && part.kind === 'systemNotification') {
    return <AhpAssistantText content={stringOrMarkdownToText(part.content) ?? ''} />
  }
  if (readOnlyHistory && part.kind === 'contentRef') {
    return <p>Referenced content was not included in the saved history: {part.uri}</p>
  }

  // contentRef and systemNotification are deferred to a later iteration.
  return null
})

/**
 * Whether a part produces visible output. This mirrors the null returns in
 * ResponsePartView above; keep the two in step. A turn whose parts all render
 * nothing must not claim to have a response, or it announces "Assistant
 * response" over an empty row.
 */
function rendersResponsePart(
  part: ResponsePart,
  hasSubagent: (toolCall: ToolCallState) => boolean,
  readOnlyHistory = false,
): boolean {
  if (part.kind === PART_MARKDOWN) return Boolean(part.content?.trim())
  if (part.kind === PART_REASONING) return Boolean(part.content?.trim())
  if (part.kind === PART_TOOL_CALL) {
    // `task_complete` is a control signal; its summary is rendered separately when it is the answer.
    if (isTaskCompletePart(part)) return false
    if (part.toolCall.toolName === DISPLAY_GITHUB_REFERENCES_TOOL_NAME) {
      if (!isSuccessfulReferenceCall(part.toolCall)) return false
      const toolInput = inlineToolInput('toolInput' in part.toolCall ? part.toolCall.toolInput : undefined)
      return parseGithubReferencesToolInput(toolInput).length > 0
    }
    if (isDeniedCancellation(part.toolCall)) return true
    if (hasSubagent(part.toolCall)) return true
    return !TOOLS_HIDDEN_FROM_TIMELINE.has(part.toolCall.toolName)
  }
  if (part.kind === PART_INPUT_REQUEST) return readOnlyHistory || Boolean(describeResolvedInputRequest(part))
  return false
}

function ClickableGitHubReferenceCards({items}: {items: ReferenceItem[]}) {
  const clickHandler = use(GitHubReferenceClickContext)

  const handleItemClick = useCallback(
    (ref: ReferenceItem, event: React.MouseEvent<HTMLAnchorElement>) => {
      if (!clickHandler) return
      clickHandler(ref, event)
    },
    [clickHandler],
  )

  return <GitHubReferenceCards items={items} onItemClick={clickHandler ? handleItemClick : undefined} />
}

function partKey(part: ResponsePart, index: number): string {
  if (part.kind === PART_TOOL_CALL) return part.toolCall.toolCallId
  if (part.kind === PART_INPUT_REQUEST) return part.request.id
  if ('id' in part) return part.id
  return `part-${index}`
}

function getTurnTokenUsage(
  turn: TimelineTurn,
): {inputTokens: number; outputTokens: number; cacheReadTokens?: number; modelId?: string} | undefined {
  const usage = turn.usage
  if (!usage) return undefined

  const hasInputTokens = typeof usage.inputTokens === 'number'
  const hasOutputTokens = typeof usage.outputTokens === 'number'
  if (!hasInputTokens && !hasOutputTokens) return undefined

  return {
    inputTokens: usage.inputTokens ?? 0,
    outputTokens: usage.outputTokens ?? 0,
    ...(typeof usage.cacheReadTokens === 'number' ? {cacheReadTokens: usage.cacheReadTokens} : {}),
    modelId: usage.model,
  }
}

interface AhpTurnViewProps {
  announceError?: boolean
  turn: Turn | ActiveTurn
  availableModels?: ModelForTokenUsage[]
  chatResource?: string
  steeringMessages?: readonly RelaySteeringMessage[]
  /**
   * Suppresses this turn's user-message bubble while preserving its response.
   * Used for the Managed CCA kickoff prompt already represented by the
   * friendly "started a task" message.
   */
  suppressUserMessage?: boolean
  SubagentComponent: AhpSubagentComponent
  subagentOffline?: boolean
  subagentChatsEnabled: boolean
  resolveSubagent?: SubagentResolver
  onContributedInstructions?: (instructions: ContributedInstructionsMetadata, trigger: HTMLElement) => void
}

const EMPTY_STEERING_MESSAGES: readonly RelaySteeringMessage[] = []

function getChannelAvatarBadge(eventType: string | null) {
  const channelOrigin = getChannelOrigin(eventType)
  if (!channelOrigin) return undefined

  if (channelOrigin === 'Slack') {
    return {
      visual: <SlackIcon size={8} data-testid="slack-icon" />,
      label: channelOrigin,
      solidBackground: true,
    }
  }

  return {
    visual: <img src={microsoftTeamsIcon} alt="" aria-hidden="true" width={12} height={12} />,
    label: channelOrigin,
  }
}

function AhpMessageTimestamp({dateTime}: {dateTime: string}) {
  const {isBrowser} = useRuntimeEnvironment()
  if (!Number.isFinite(Date.parse(dateTime))) return null

  const formattedTimestamp = isBrowser
    ? new Date(dateTime).toLocaleString(undefined, {dateStyle: 'full', timeStyle: 'medium'})
    : new Date(dateTime).toUTCString()

  return (
    <Tooltip text={formattedTimestamp} type="description">
      <Button variant="invisible" size="small" className={styles.MessageTimestamp}>
        <RelativeTime datetime={dateTime} format="micro" tense="past" noTitle />
      </Button>
    </Tooltip>
  )
}

function AhpUserMessageBubble({createdAt, ...props}: ComponentProps<typeof UserMessageBubble> & {createdAt?: string}) {
  const {session} = useSessionContext()

  return (
    <div className={styles.UserMessage}>
      <UserMessageBubble
        {...props}
        showAvatar={!!getChannelOrigin(session.event_type)}
        footer={
          <div className={styles.UserMessageActions} role="group" aria-label="User message actions">
            {createdAt && <AhpMessageTimestamp dateTime={createdAt} />}
            {props.content?.trim() && (
              <CopyToClipboardIconButton size="small" textToCopy={props.content} ariaLabel="Copy message" />
            )}
          </div>
        }
      />
    </div>
  )
}

const SteeringMessageView = memo(function SteeringMessage({message}: {message: RelaySteeringMessage}) {
  const userText = message.message?.text
  const userImages = useMemo(() => extractUserMessageImages(message.message), [message.message])
  const chipAttachments = useMemo(() => extractChipAttachments(message.message), [message.message])
  const usesPendingBubbleStyle = message.status !== 'handed-off'

  return (
    <>
      <VisuallyHidden>
        {message.status === 'pending' ? 'Steering message waiting for agent' : 'Steering message'}
      </VisuallyHidden>
      <div data-testid={`ahp-steering-message-${message.id}`}>
        <AhpUserMessageBubble
          content={userText}
          images={userImages}
          attachments={chipAttachments}
          bubbleClassName={usesPendingBubbleStyle ? styles.pendingSteeringBubble : undefined}
        />
      </div>
    </>
  )
})

const TurnResponseContent = memo(function ResponseContent({
  responseParts,
  presentation,
  steeringMessages,
  chatResource,
  SubagentComponent,
  subagentOffline,
  subagentChatsEnabled,
  resolveSubagent,
  isActiveTurn,
  readOnlyHistory,
  taskCompletion,
  hasSubagent,
  registerGroupTrigger,
  groupingEnabled,
  verbosity,
}: {
  responseParts: ResponsePart[]
  presentation?: CopilotResultRenderIdentity
  steeringMessages: readonly RelaySteeringMessage[]
  chatResource?: string
  SubagentComponent: AhpSubagentComponent
  subagentOffline?: boolean
  subagentChatsEnabled: boolean
  resolveSubagent?: SubagentResolver
  isActiveTurn: boolean
  readOnlyHistory: boolean
  taskCompletion: TaskCompletionResolution
  hasSubagent: (toolCall: ToolCallState) => boolean
  registerGroupTrigger: RegisterGroupTrigger
  groupingEnabled: boolean
  verbosity: TimelineVerbosity
}) {
  const minimal = verbosity === 'minimal'
  const detailed = verbosity === 'detailed'
  const steeringByResponsePartIndex = useMemo(() => {
    const messagesByIndex = new Map<number, RelaySteeringMessage[]>()
    for (const message of steeringMessages) {
      const index = Math.min(Math.max(message.responsePartIndex, 0), responseParts.length)
      const messages = messagesByIndex.get(index)
      if (messages) {
        messages.push(message)
      } else {
        messagesByIndex.set(index, [message])
      }
    }
    return messagesByIndex
  }, [responseParts.length, steeringMessages])

  const [rowMemory] = useState(createToolDisclosureMemoryStore)
  const [groupMemory] = useState(createToolDisclosureMemoryStore)

  useEffect(() => {
    const handleGlobalToggle = (event: Event) => {
      rowMemory.writeAll((event as CustomEvent<{expand: boolean}>).detail.expand)
    }
    document.addEventListener('mission-control-tool-global-toggle', handleGlobalToggle)
    return () => document.removeEventListener('mission-control-tool-global-toggle', handleGlobalToggle)
  }, [rowMemory])

  const renderPart = (part: ResponsePart, index: number, includeSteering = true) => {
    const messages = includeSteering ? steeringByResponsePartIndex.get(index) : undefined
    const content =
      index === taskCompletion.completionIndex && taskCompletion.answerSummary ? (
        <AhpAssistantText
          content={taskCompletion.answerSummary}
          presentation={presentation ? {...presentation, part_id: partKey(part, index)} : undefined}
        />
      ) : (
        <ResponsePartView
          part={part}
          presentation={presentation}
          chatResource={chatResource}
          SubagentComponent={SubagentComponent}
          subagentOffline={subagentOffline}
          subagentChatsEnabled={subagentChatsEnabled}
          isStreaming={isActiveTurn && index === responseParts.length - 1}
          readOnlyHistory={readOnlyHistory}
          detailed={groupingEnabled ? detailed : undefined}
          {...(resolveSubagent ? {resolveSubagent} : {})}
        />
      )

    return (
      <Fragment key={partKey(part, index)}>
        {messages?.map(message => (
          <SteeringMessageView key={message.id} message={message} />
        ))}
        {groupingEnabled &&
        (part.kind === PART_TOOL_CALL || part.kind === PART_REASONING || part.kind === PART_INPUT_REQUEST) &&
        (rendersResponsePart(part, hasSubagent, readOnlyHistory) ||
          (index === taskCompletion.completionIndex && Boolean(taskCompletion.answerSummary))) ? (
          <div
            data-ahp-part-key={partKey(part, index)}
            className={
              part.kind === PART_TOOL_CALL &&
              !(index === taskCompletion.completionIndex && taskCompletion.answerSummary)
                ? styles.toolPart
                : undefined
            }
          >
            <ToolDisclosureMemoryContext value={rowMemory.memoryFor(partKey(part, index))}>
              {content}
            </ToolDisclosureMemoryContext>
          </div>
        ) : (
          content
        )}
      </Fragment>
    )
  }
  const [previousRunIds, setPreviousRunIds] = useState<ReadonlyMap<number, string>>(NO_RUN_IDS)
  const segmented = useMemo(() => {
    if (!groupingEnabled) return undefined
    const entries = responseParts.map((part, index) => ({part, index}))
    const completionIndex = taskCompletion.answerSummary ? taskCompletion.completionIndex : undefined
    const hasSteering = (index: number) => steeringByResponsePartIndex.has(index)
    const isVisible = ({part, index}: ResponsePartEntry) =>
      rendersResponsePart(part, hasSubagent, readOnlyHistory) || index === completionIndex
    const deferFrom = isActiveTurn ? trailingBlockStart(entries, hasSteering, completionIndex) : entries.length
    const live = isActiveTurn
    return segmentResponseParts(entries, {isVisible, completionIndex, hasSteering, live, previousRunIds}).map(
      section => ({
        section,
        grouped: new Map(
          section.items.flatMap(item =>
            item.kind === 'run'
              ? [
                  [
                    item.id,
                    (item.items[0]?.index ?? 0) >= deferFrom
                      ? item.items.map(entry => ({kind: 'part' as const, ...entry}))
                      : groupResponseParts(item.items, {hasSubagent, completionIndex, hasSteering}),
                  ] as const,
                ]
              : [],
          ),
        ),
      }),
    )
  }, [
    groupingEnabled,
    isActiveTurn,
    readOnlyHistory,
    responseParts,
    hasSubagent,
    taskCompletion.answerSummary,
    taskCompletion.completionIndex,
    steeringByResponsePartIndex,
    previousRunIds,
  ])
  const runIds = useMemo(
    () =>
      new Map(
        segmented?.flatMap(({section}) =>
          section.items.flatMap(item =>
            item.kind === 'run' ? item.items.map(entry => [entry.index, item.id] as const) : [],
          ),
        ),
      ),
    [segmented],
  )
  if (!sameRunIds(runIds, previousRunIds)) setPreviousRunIds(runIds)
  const collapsedLiveRuns = useMemo(
    () =>
      minimal && isActiveTurn && segmented
        ? segmented.flatMap(({section}) =>
            section.items.flatMap(item => (item.kind === 'run' && item.toolCalls.length > 0 ? [item] : [])),
          )
        : NO_RUNS,
    [minimal, isActiveTurn, segmented],
  )
  const turnToolCalls = useMemo(
    () => responseParts.flatMap(part => (part.kind === PART_TOOL_CALL ? [part.toolCall] : [])),
    [responseParts],
  )
  useLiveRunCompletionAnnouncements(collapsedLiveRuns, turnToolCalls, minimal)
  const groupedParts = useMemo(() => segmented?.flatMap(({grouped}) => [...grouped.values()].flat()), [segmented])
  useGroupedCodeSearchAnnouncements(responseParts, groupedParts)

  const renderSteeringBefore = (index: number | undefined) =>
    steeringByResponsePartIndex
      .get(index ?? -1)
      ?.map(message => <SteeringMessageView key={message.id} message={message} />)

  const renderGrouped = (items: readonly GroupedResponsePart[]) =>
    items.map(item =>
      item.kind === 'group' ? (
        <AhpToolCallGroup
          key={item.id}
          group={item}
          defaultExpanded={detailed}
          chatResource={chatResource}
          presentation={presentation}
          readOnlyHistory={readOnlyHistory}
          disclosureMemoryFor={groupMemory.memoryFor}
          registerGroupTrigger={registerGroupTrigger}
        >
          {item.items.map(({part, index}) => renderPart(part, index, false))}
        </AhpToolCallGroup>
      ) : (
        renderPart(item.part, item.index, false)
      ),
    )

  const renderSectionItem = (item: ResponseSectionItem, grouped: ReadonlyMap<string, GroupedResponsePart[]>) =>
    item.kind === 'run' ? (
      <AhpToolCallSummary
        summary={item}
        collapsed={minimal && isActiveTurn && item.toolCalls.length > 0}
        chatResource={chatResource}
        presentation={presentation}
        readOnlyHistory={readOnlyHistory}
        registerGroupTrigger={registerGroupTrigger}
      >
        {renderGrouped(grouped.get(item.id) ?? [])}
      </AhpToolCallSummary>
    ) : (
      renderPart(item.part, item.index, false)
    )

  return (
    <>
      {segmented
        ? segmented.map(({section, grouped}) => (
            <Fragment key={section.id}>
              {renderSteeringBefore(section.firstIndex)}
              <AhpResponseSection
                section={section}
                collapsed={minimal && !isActiveTurn && section.toolCalls.length > 0}
                chatResource={chatResource}
                presentation={presentation}
                readOnlyHistory={readOnlyHistory}
                registerGroupTrigger={registerGroupTrigger}
                slots={section.items.map(item => ({
                  key: item.kind === 'run' ? item.id : partKey(item.part, item.index),
                  focusKey: item.kind === 'run' ? undefined : partKey(item.part, item.index),
                  hideable: item.hideable,
                  visible: item.visible,
                  node: renderSectionItem(item, grouped),
                }))}
              />
            </Fragment>
          ))
        : responseParts.map((part, index) => renderPart(part, index))}
      {steeringByResponsePartIndex.get(responseParts.length)?.map(message => (
        <SteeringMessageView key={message.id} message={message} />
      ))}
    </>
  )
})

const NO_RUN_IDS: ReadonlyMap<number, string> = new Map()
const NO_RUNS: readonly ResponseRun[] = []

function sameRunIds(next: ReadonlyMap<number, string>, previous: ReadonlyMap<number, string>) {
  return next.size === previous.size && [...next].every(([index, id]) => previous.get(index) === id)
}

const MAX_FOCUS_RETRY_FRAMES = 10

function focusWhenAvailable(target: HTMLElement, previous: HTMLElement, frame = 0) {
  if (!target.isConnected) return
  if (!target.closest('[inert]')) {
    target.focus()
    return
  }
  if (frame >= MAX_FOCUS_RETRY_FRAMES) return
  requestAnimationFrame(() => {
    const {activeElement, body, documentElement} = target.ownerDocument
    if (activeElement && activeElement !== previous && activeElement !== body && activeElement !== documentElement) {
      return
    }
    focusWhenAvailable(target, previous, frame + 1)
  })
}

/** Renders one AHP turn: user message, response parts in order, then any terminal error. */
export const AhpTurnView = memo(function AhpTurnViewComponent({
  turn,
  announceError = true,
  availableModels,
  chatResource,
  steeringMessages = EMPTY_STEERING_MESSAGES,
  suppressUserMessage = false,
  SubagentComponent,
  subagentOffline,
  subagentChatsEnabled,
  resolveSubagent,
  onContributedInstructions,
}: AhpTurnViewProps) {
  const {session} = useSessionContext()
  const {alignToolbarWithMessage} = useSessionLogsContext()
  const readOnlyHistory = useReadOnlyAhpHistory()
  const initialMessage = use(LegacyChatInitialMessageContext)
  const userText = turn.message?.text
  const userImages = useMemo(() => extractUserMessageImages(turn.message), [turn.message])
  const chipAttachments = useMemo(() => extractChipAttachments(turn.message), [turn.message])
  const space = extractTurnSpace(turn.message)
  const shouldShowUserMessage = useShouldShowUserMessage()
  const turnActor = useMemo(() => extractTurnActor(turn.message), [turn.message])
  const actorProfile = useActorProfileState(turnActor?.githubId)
  const messagingAppAvatarBadge = getChannelAvatarBadge(session.event_type)
  // Channel sessions can also be steered from the web, so only badge turns the channel client stamped.
  const turnMessageType = extractTurnMessageType(turn.message)
  const avatarBadge = messagingAppAvatarBadge && (turnActor || turnMessageType) ? messagingAppAvatarBadge : undefined
  // The CLI injects skill instructions as a synthetic user message; the timeline surfaces that
  // content as its own tool-call row, so suppress the duplicate user bubble it would render.
  const isInjectedMessage = isSkillContextMessage(userText)
  const backgroundShellNotification = isSystemNotificationMessage(turn.message)
    ? parseBackgroundShellNotification(turn.message?.text)
    : undefined
  const hasReplacementBackgroundActivity = useBackgroundActivityTaskPresent(
    session.id,
    backgroundShellNotification?.shellId,
  )
  const isBackgroundShellNotification = Boolean(backgroundShellNotification) && hasReplacementBackgroundActivity
  const hasUserMessage =
    shouldShowUserMessage(turn.message) &&
    !(messagingAppAvatarBadge && turnMessageType === 'system') &&
    !suppressUserMessage &&
    !isSystemAuthoredMessage(turn.message) &&
    !isBackgroundShellNotification &&
    !isInjectedMessage &&
    (Boolean(userText) || userImages.length > 0 || chipAttachments.length > 0 || Boolean(space))
  useLayoutEffect(() => {
    if (!initialMessage || !hasUserMessage || userText?.trim() !== initialMessage.text.trim()) return
    if (
      !chipAttachments.some(
        attachment =>
          attachment.type === 'embeddedResource' && attachment.label === initialMessage.transcriptAttachmentLabel,
      )
    ) {
      return
    }
    initialMessage.report(turn.id, true)
    return () => initialMessage.report(turn.id, false)
  }, [chipAttachments, hasUserMessage, initialMessage, turn.id, userText])
  const responseParts = turn.responseParts
  const isActiveTurn = !readOnlyHistory && !('state' in turn)
  const groupingEnabled = isFeatureEnabled('one_copilot_grouped_tool_calls')
  const selectedVerbosity = use(TimelineVerbosityContext)
  const verbosity = groupingEnabled ? selectedVerbosity : 'balanced'
  const previousIsActiveTurnRef = useRef(isActiveTurn)
  const isCompleteTurn = 'state' in turn && turn.state === TURN_STATE_COMPLETE
  const hasReasoning = responseParts.some(part => part.kind === PART_REASONING && Boolean(part.content?.trim()))

  useEffect(() => {
    if (previousIsActiveTurnRef.current && isCompleteTurn && hasReasoning && !readOnlyHistory) {
      announce('Reasoning complete', {assertive: false})
    }
    previousIsActiveTurnRef.current = isActiveTurn
  }, [hasReasoning, isActiveTurn, isCompleteTurn, readOnlyHistory])
  const hasHideableToolCall = useMemo(
    () =>
      responseParts.some(
        part => part.kind === PART_TOOL_CALL && TOOLS_HIDDEN_FROM_TIMELINE.has(part.toolCall.toolName),
      ),
    [responseParts],
  )
  const liveSubagentPresent = useSubagentPresence(
    session.task_id,
    chatResource,
    subagentChatsEnabled && !resolveSubagent && (hasHideableToolCall || groupingEnabled),
  )
  const hasSubagent = useCallback(
    (toolCall: ToolCallState) =>
      resolveSubagent ? Boolean(resolveSubagent(toolCall, chatResource)) : liveSubagentPresent(toolCall),
    [resolveSubagent, chatResource, liveSubagentPresent],
  )
  const isVisiblePart = useCallback((part: ResponsePart) => rendersResponsePart(part, hasSubagent), [hasSubagent])
  const taskCompletion = useMemo(
    () => resolveTaskCompletion(responseParts, isVisiblePart),
    [responseParts, isVisiblePart],
  )
  const hasAssistantResponse = Boolean(taskCompletion.answerSummary) || responseParts.some(isVisiblePart)
  const hasResponse = hasAssistantResponse || steeringMessages.length > 0
  const copyableResponseText = useMemo(
    () => getCopyableResponseText(responseParts, isVisiblePart, taskCompletion),
    [responseParts, isVisiblePart, taskCompletion],
  )
  const hasFeedbackEligibleResponse = copyableResponseText.trim().length > 0
  const tokenUsage = useMemo(() => getTurnTokenUsage(turn), [turn])
  const canShowTokenUsage = Boolean(tokenUsage)
  const canShowFeedback = isCompletedTurn(turn) && hasFeedbackEligibleResponse
  const completedAt = getTurnCompletedAt(turn)
  const contributedInstructions = useMemo(
    () => (isCompletedTurn(turn) ? extractTurnContributedInstructions(turn.message) : undefined),
    [turn],
  )
  const showCopyAction = canShowFeedback
  const turnFeedbackSource = `session-log:session_id=${session.id},turn_id=${turn.id}`
  const turnError = isErroredTurn(turn) ? getTurnError(turn) : undefined
  const hasError = Boolean(turnError)
  const terminalState = 'state' in turn ? turn.state : undefined
  const presentation = useMemo<CopilotResultRenderIdentity>(
    () => ({
      task_id: session.task_id,
      session_id: session.id,
      environment_id: session.environment_id ?? undefined,
      chat_id: chatResource,
      turn_id: turn.id,
      ...(terminalState ? {terminal_state: terminalState} : {}),
    }),
    [session.task_id, session.id, session.environment_id, chatResource, turn.id, terminalState],
  )
  const terminalErrorRef = useCopilotResultElement(presentation, 'terminal', !readOnlyHistory && hasError)
  const turnRef = useRef<HTMLLIElement | null>(null)
  const focusedPartKeysRef = useRef<string[] | null>(null)
  const focusedPartElementRef = useRef<HTMLElement | null>(null)
  const groupTriggersRef = useRef(new Map<HTMLButtonElement, readonly string[]>())
  const attachTurn = useCallback((node: HTMLLIElement | null) => {
    turnRef.current = node
    if (!node) return
    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target
      let keys: string[] = []
      if (target instanceof HTMLElement) {
        let part = target.closest<HTMLElement>('[data-ahp-part-key]')
        while (part && part.closest('[data-turn-id]') !== node) {
          part = part.parentElement?.closest<HTMLElement>('[data-ahp-part-key]') ?? null
        }
        const key = part?.getAttribute('data-ahp-part-key')
        const triggerKeys = target instanceof HTMLButtonElement ? groupTriggersRef.current.get(target) : undefined
        keys = triggerKeys ? [...triggerKeys] : key ? [key] : []
      }
      focusedPartKeysRef.current = keys.length > 0 ? keys : null
      focusedPartElementRef.current = keys.length > 0 && target instanceof HTMLElement ? target : null
    }
    const handleFocusOut = (event: FocusEvent) => {
      if (event.relatedTarget instanceof Node && !node.contains(event.relatedTarget)) {
        focusedPartKeysRef.current = null
        focusedPartElementRef.current = null
      } else if (event.relatedTarget === null) {
        const element = focusedPartElementRef.current
        queueMicrotask(() => {
          if (element?.isConnected && focusedPartElementRef.current === element) {
            focusedPartKeysRef.current = null
            focusedPartElementRef.current = null
          }
        })
      }
    }
    node.addEventListener('focusin', handleFocusIn)
    node.addEventListener('focusout', handleFocusOut)
    return () => {
      node.removeEventListener('focusin', handleFocusIn)
      node.removeEventListener('focusout', handleFocusOut)
      turnRef.current = null
      focusedPartKeysRef.current = null
      focusedPartElementRef.current = null
    }
  }, [])
  const registerGroupTrigger = useCallback<RegisterGroupTrigger>((items, trigger) => {
    const keys = items.map(({part, index}) => partKey(part, index))
    groupTriggersRef.current.set(trigger, keys)
    return () => {
      if (groupTriggersRef.current.get(trigger) === keys) groupTriggersRef.current.delete(trigger)
    }
  }, [])
  useLayoutEffect(() => {
    if (!groupingEnabled) return
    const keys = focusedPartKeysRef.current
    const element = focusedPartElementRef.current
    const node = turnRef.current
    if (!keys || !element || (element.isConnected && !element.closest('[inert]')) || !node) return
    focusedPartKeysRef.current = null
    focusedPartElementRef.current = null
    const active = node.ownerDocument.activeElement
    if (
      active &&
      active !== element &&
      active !== node.ownerDocument.body &&
      active !== node.ownerDocument.documentElement
    ) {
      return
    }
    const trigger = [...groupTriggersRef.current]
      .filter(
        ([candidate, candidateKeys]) =>
          candidate.isConnected &&
          !candidate.closest('[data-ahp-concealed]') &&
          candidateKeys.some(key => keys.includes(key)),
      )
      .sort(([, a], [, b]) => a.length - b.length)[0]?.[0]
    if (trigger) {
      focusWhenAvailable(trigger, element)
      return
    }
    const parts = [...node.querySelectorAll<HTMLElement>('[data-ahp-part-key]')].filter(
      part => part.closest('[data-turn-id]') === node,
    )
    let target: HTMLElement | undefined
    for (const key of keys) {
      const part = parts.find(candidate => candidate.getAttribute('data-ahp-part-key') === key)
      if (!part) continue
      target = [
        ...part.querySelectorAll<HTMLElement>(
          'button:enabled, a[href], input:enabled:not([type="hidden"]), select:enabled, textarea:enabled, summary, [tabindex]:not(:disabled)',
        ),
      ].find(candidate => !candidate.closest('[inert]'))
      if (target) break
    }
    const focusTarget = target ?? node
    focusTarget.focus()
  })

  // A suppressed message can leave a turn with nothing to show. Rendering it anyway adds an empty
  // list item that screen readers announce as a conversation turn, and that still takes flex gap.
  if (!hasUserMessage && !hasResponse && !hasError) {
    return null
  }

  return (
    <li
      data-turn-id={turn.id}
      tabIndex={-1}
      className={turnStyles.turn}
      {...(groupingEnabled ? {ref: attachTurn} : {})}
    >
      <VisuallyHidden>Conversation turn {getTurnStatusLabel(turn)}</VisuallyHidden>
      {hasUserMessage && (
        <>
          <VisuallyHidden>User message</VisuallyHidden>
          <AhpUserMessageBubble
            content={userText}
            createdAt={turn.startedAt}
            images={userImages}
            attachments={chipAttachments}
            space={space}
            avatar={actorProfile?.kind === 'resolved' ? actorProfile.avatar : undefined}
            avatarLoading={actorProfile?.kind === 'loading'}
            avatarBadge={avatarBadge}
          />
        </>
      )}

      {hasResponse &&
        (canShowFeedback || groupingEnabled ? (
          <div
            className={canShowFeedback ? styles.responseContainer : styles.responseContents}
            data-align-toolbar-with-message={canShowFeedback && alignToolbarWithMessage ? 'true' : undefined}
          >
            {hasAssistantResponse && <VisuallyHidden>Assistant response</VisuallyHidden>}
            <TurnResponseContent
              responseParts={responseParts}
              presentation={readOnlyHistory ? undefined : presentation}
              steeringMessages={steeringMessages}
              chatResource={chatResource}
              SubagentComponent={SubagentComponent}
              subagentOffline={subagentOffline}
              subagentChatsEnabled={subagentChatsEnabled}
              isActiveTurn={isActiveTurn}
              readOnlyHistory={readOnlyHistory}
              taskCompletion={taskCompletion}
              hasSubagent={hasSubagent}
              registerGroupTrigger={registerGroupTrigger}
              groupingEnabled={groupingEnabled}
              verbosity={verbosity}
              {...(resolveSubagent ? {resolveSubagent} : {})}
            />
            {canShowFeedback && (
              <Toolbar aria-label="Message actions" className={styles.actions}>
                {canShowFeedback && <SessionFeedbackButtons feedbackSource={turnFeedbackSource} />}
                {canShowTokenUsage && tokenUsage && (
                  <MessageTokenUsageWithData
                    usage={tokenUsage}
                    modelId={tokenUsage.modelId}
                    availableModels={availableModels}
                    mode="immersive"
                    variant="info"
                  />
                )}
                {onContributedInstructions && contributedInstructions && (
                  <AppliedInstructionsMenu
                    contributedInstructions={contributedInstructions}
                    onSelect={(instruction, trigger) => onContributedInstructions([instruction], trigger)}
                  />
                )}
                {showCopyAction && (
                  <CopyToClipboardIconButton
                    size="small"
                    textToCopy={copyableResponseText}
                    variant="invisible"
                    ariaLabel="Copy response"
                  />
                )}
                {completedAt && <AhpMessageTimestamp dateTime={completedAt} />}
              </Toolbar>
            )}
          </div>
        ) : (
          <>
            {hasAssistantResponse && <VisuallyHidden>Assistant response</VisuallyHidden>}
            <TurnResponseContent
              responseParts={responseParts}
              presentation={readOnlyHistory ? undefined : presentation}
              steeringMessages={steeringMessages}
              chatResource={chatResource}
              SubagentComponent={SubagentComponent}
              subagentOffline={subagentOffline}
              subagentChatsEnabled={subagentChatsEnabled}
              isActiveTurn={isActiveTurn}
              readOnlyHistory={readOnlyHistory}
              taskCompletion={taskCompletion}
              hasSubagent={hasSubagent}
              registerGroupTrigger={registerGroupTrigger}
              groupingEnabled={groupingEnabled}
              verbosity={verbosity}
              {...(resolveSubagent ? {resolveSubagent} : {})}
            />
          </>
        ))}
      {hasError && turnError && (
        <div className={styles.errorNotice} role={announceError ? 'alert' : undefined} ref={terminalErrorRef}>
          <AlertIcon size={16} className={styles.errorIcon} aria-hidden="true" />
          <span>{turnError.message}</span>
        </div>
      )}
    </li>
  )
})
